import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {
  ChangePasswordResult,
  InviteUserDto,
  UpdateUserDto,
  User,
  UserDto,
  UserExistsBaseDto,
  UserExistsDto,
  UserImportIssue,
  UserImportResult,
  UserImportResultDto,
  UserMigrationResult
} from '../model/auth/User';
import {Role} from '../model/auth/Role';
import {DEFAULT_LOCALE, Locale} from '../model/Locale';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private http: HttpClient) {
  }

  private static readonly ID = 'USER_ID';
  private static readonly EMAIL = 'EMAIL';

  private static readonly USERS_BASE_URL = '/users';
  private static readonly USERS_DETAILS_URL = `${UserService.USERS_BASE_URL}/${UserService.ID}`;
  private static readonly USERS_INVITE_URL = `${UserService.USERS_BASE_URL}/invite`;
  private static readonly USERS_IMPORT_URL = `${UserService.USERS_BASE_URL}/import`;
  private static readonly USERS_MIGRATE_URL = `${UserService.USERS_BASE_URL}/migrate`;
  private static readonly USERS_RESET_URL = `${UserService.USERS_BASE_URL}/reset`;
  private static readonly USERS_RESET_AUTH_URL = `${UserService.USERS_RESET_URL}/auth`;
  private static readonly USERS_INVITE_RESEND_URL = `${UserService.USERS_INVITE_URL}/resend`;
  private static readonly USERS_SEARCH_URL = `${UserService.USERS_BASE_URL}/search?query=`;
  private static readonly USERS_EXIST_URL = `${UserService.USERS_BASE_URL}/search/${UserService.EMAIL}`;
  private static readonly USERS_EXIST_AUTH_URL = `${UserService.USERS_EXIST_URL}/auth`;

  searchUsers(query: string, limit = 5): Observable<User[]> {
    const url = UserService.USERS_SEARCH_URL + encodeURIComponent(query) + '&limit=' + limit.toString();

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as UserDto[]).map(item => User.parse(item)))
      );
  }

  userExists(identifier: string, verifyByUsername = false): Observable<UserExistsBaseDto> {
    let url = UserService.USERS_EXIST_URL.replace(UserService.EMAIL, encodeURIComponent(identifier.trim()));
    if (verifyByUsername) {
      url += '?verify_username=true';
    }

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => data as UserExistsBaseDto)
      );
  }

  userExistsAuth(email: string): Observable<UserExistsDto> {
    const url = UserService.USERS_EXIST_AUTH_URL.replace(UserService.EMAIL, encodeURIComponent(email.trim()));

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => data as UserExistsDto)
      );
  }

  inviteUser(data: InviteUserDto): Observable<User> {
    return this.http.post(`${environment.api.host}${UserService.USERS_INVITE_URL}`, data)
      .pipe(
        map(data => User.parse(data as UserDto))
      );
  }

  inviteUserResend(data: InviteUserDto): Observable<User> {
    return this.http.post(`${environment.api.host}${UserService.USERS_INVITE_RESEND_URL}`, data)
      .pipe(
        map(data => User.parse(data as UserDto))
      );
  }

  importUsers(fileUrl: string, role: Role, regionId: string, schoolId: string,
              contestId: string, competitionId: string, locale: Locale = DEFAULT_LOCALE): Observable<UserImportResult> {
    interface UsersImport {
      file_url: string;
      role: Role;
      region_id: string;
      school_id: string;
      locale: Locale;

      competition_id?: string;
      contest_id?: string;
    }

    const data: UsersImport = {
      file_url: fileUrl,
      role: role,
      region_id: regionId,
      school_id: schoolId,
      locale: locale,
      contest_id: contestId,
      competition_id: competitionId,
    };

    return this.http.post(`${environment.api.host}${UserService.USERS_IMPORT_URL}`, data)
      .pipe(
        map(data => {
          const result = data as UserImportResultDto;
          const issues: { [key in UserImportIssue]?: User[] } = {};
          Object.keys(result.issues || {}).forEach(issue => {
            if (result.issues && result.issues[<UserImportIssue>issue]?.length) {
              issues[<UserImportIssue>issue] =
                result.issues[<UserImportIssue>issue]?.map(user => User.parse(user));
            }
          });

          return {
            users: result.users.map(user => User.parse(user)),
            issues,
          };
        })
      );
  }

  forceResetUserPassword(email: string, tenant_name?: string, tenant_domain?: string): Observable<any> {
    interface UserResetDto {
      email: string;
      tenant_name?: string;
      tenant_domain?: string;
    }

    const data: UserResetDto = {
      email: email,
      tenant_name: tenant_name,
      tenant_domain: tenant_domain,
    };

    return this.http.post(`${environment.api.host}${UserService.USERS_RESET_URL}`, data);
  }

  changeUserPasswords(usernames: string[], tmpPassword = true): Observable<ChangePasswordResult[]> {
    interface UserPasswordDto {
      usernames: string[];
      tmp_password: boolean;
    }

    const data: UserPasswordDto = {
      usernames: usernames,
      tmp_password: tmpPassword
    };

    return this.http.post(`${environment.api.host}${UserService.USERS_RESET_AUTH_URL}`, data)
      .pipe(
        map(data => data as ChangePasswordResult[])
      );
  }

  getUserDetails(userId: string): Observable<User> {
    const url = UserService.USERS_DETAILS_URL.replace(UserService.ID, userId) + '?local=true';

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => User.parse(data as UserDto))
      );
  }

  updateUserDetails(userId: string, data: UpdateUserDto, competitionId?: string): Observable<User> {
    const url = (UserService.USERS_DETAILS_URL.replace(UserService.ID, userId))
      + (competitionId ? `?competition_id=${competitionId}` : '');

    return this.http.put(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => User.parse(data as UserDto))
      );
  }

  reportUpdateUser(userId: string, propagate = true, confirm_email?: boolean): Observable<object> {
    const data = {
      propagate: propagate,
      ...(confirm_email && {confirm_email})
    };
    return this.updateUserDetails(userId, data);
  }

  migrateUser(resetFlow: boolean, userId: string, password?: string): Observable<UserMigrationResult> {
    const data = {
      username: userId,
      password: password,
      reset: resetFlow,
    };

    return this.http.post(`${environment.api.host}${UserService.USERS_MIGRATE_URL}`, data)
      .pipe(
        map(data => data as UserMigrationResult)
      );
  }

}
